import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import {
	Asset,
	AssetType,
	getAssetDescription,
	overarchingTypeOf,
	RealEstate,
	RealEstateFinancingType,
	translateAssetType,
	translateFinancingType,
	translatePropellant,
	translateYearlyMileage,
	Vehicle,
} from '../AssetOverview.types'
import {
	Accordion,
	AccordionDetails,
	BoxInput,
	IconButton,
	MaterialIcons,
	Select,
	useIntlConfig,
} from '@monthio/react-components'
import { AssetLogo } from './assetLogo'
import { useTranslation } from 'react-i18next'
import {
	SelectPlaceholderLabel,
	SelectYearlyMileage,
	useStyles,
} from './AddOrEditAsset/AddAsset'
import { useDeleteAssetModal } from './DeleteAssetModal'
import { ConditionalWrapper } from '../../../modulesTemp/shared/conditionalWrapper'
import { DeletedOverlay } from '../../../modulesTemp/shared/deletedOverlay'
import { ItemWithOptionalLogo } from '../../../modulesTemp/shared/ItemWithOptionalLogo'
import { H3 } from '@monthio/design-system'

const ManualInputContainer = styled.div`
	display: flex;
	justify-content: space-between;
	background: #f9fafb;
	align-items: center;
	width: 100%;
	padding: 0px 12px;
`

export const FieldsBackground = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 2px 0;
	margin-top: 2px;

	> div {
		background: var(--ColorTokens-Shapes-primary-surface, #fff);
		padding: var(--spacingSystem-space-s, 12px);
	}

	> div:nth-of-type(2n) {
		text-align: right;
	}

	> div:nth-last-of-type(2) {
		border-bottom-left-radius: var(--borderRadius-borderRadius-xs, 4px);
	}

	> div:last-of-type {
		border-bottom-right-radius: var(--borderRadius-borderRadius-xs, 4px);
	}
	width: 100%;
`

function useDescriptionFieldsOf(
	asset: Asset,
): { title: string; value?: string }[] {
	const { t } = useTranslation()
	switch (overarchingTypeOf(asset.assetType)) {
		case 'realEstate':
			const realEstate = asset as RealEstate
			return [
				{
					title: t('asset-overview.real-estate.address'),
					value: realEstate.address,
				},
				{
					title: t('asset-overview.real-estate.financing-type'),
					value: translateFinancingType(
						realEstate.financingType,
						realEstate.assetType,
					),
				},
			]
		case 'vehicle':
			const vehicle = asset as Vehicle
			return [
				{
					title: t('asset-overview.vehicle.brand'),
					value: vehicle.brand,
				},
				{
					title: t('asset-overview.vehicle.model'),
					value: vehicle.model,
				},
				{
					title: t('asset-overview.vehicle.variant'),
					value: vehicle.variant,
				},
				{
					title: t('asset-overview.vehicle.modelYear'),
					value: vehicle.year.toFixed(0),
				},
				{
					title: t('asset-overview.vehicle.propellant'),
					value: translatePropellant(vehicle.propellant),
				},
				{
					title: t('asset-overview.vehicle.financing-type'),
					value: translateFinancingType(
						vehicle.financingType,
						vehicle.assetType,
					),
				},
				...(vehicle.assetType !== AssetType.CampingWagon
					? [
							{
								title: t('asset-overview.vehicle.yearly-mileage'),
								value: translateYearlyMileage(vehicle.yearlyMileage),
							},
						]
					: []),
			]
		default:
			return []
	}
}

export function SelectFinancingType({
	asset,
	onChange,
	size = 'default',
}: {
	asset: RealEstate
	onChange: (financingType: RealEstateFinancingType) => void
	size?: 'small' | 'default'
}): JSX.Element | null {
	const classes = useStyles()
	const menuProps = useMemo(
		() => ({
			MenuListProps: {
				className: classes.menuList,
			},
		}),
		[classes.menuList],
	)

	return (
		<Select
			className={size === 'small' ? classes.root : undefined}
			style={
				size === 'small'
					? { minWidth: '173px', height: '35px' }
					: { minWidth: '250px' }
			}
			value={asset.financingType ?? ''}
			values={Object.values(RealEstateFinancingType).map((value) => ({
				value,
				displayValue: translateFinancingType(value, asset.assetType),
			}))}
			onChange={(e) => onChange(e.target.value as RealEstateFinancingType)}
			variant="outlined"
			displayEmpty
			renderValue={(selected) => (
				<SelectPlaceholderLabel isSelected={!!selected}>
					{translateFinancingType(
						(selected as RealEstateFinancingType) ||
							RealEstateFinancingType.None,
						asset.assetType,
					)}
				</SelectPlaceholderLabel>
			)}
			MenuProps={size === 'small' ? menuProps : undefined}
		/>
	)
}

type FooterProps = {
	asset: Vehicle | RealEstate
	onChange?: (asset: Asset) => void
}

const VehicleFooter: React.FC<FooterProps> = ({ asset, onChange }) => {
	const { t } = useTranslation()
	const vehicleAsset = asset as Vehicle
	return (
		<ManualInputContainer>
			<H3>{t('asset-overview.asset-card.yearly-mileage-label')}</H3>
			<SelectYearlyMileage
				asset={vehicleAsset}
				onChange={(yearlyMileage) => {
					onChange &&
						onChange({
							...vehicleAsset,
							yearlyMileage,
						} as Asset)
				}}
				size="small"
			/>
		</ManualInputContainer>
	)
}

const RealEstateFooter: React.FC<FooterProps> = ({ asset, onChange }) => {
	const { t } = useTranslation()
	const realEstateAsset = asset as RealEstate
	return (
		<ManualInputContainer>
			<H3>{t('asset-overview.real-estate.financing-type')}</H3>
			<SelectFinancingType
				asset={realEstateAsset}
				onChange={(ownershipType) => {
					onChange?.({
						...realEstateAsset,
						financingType: ownershipType,
					} as Asset)
				}}
				size="small"
			/>
		</ManualInputContainer>
	)
}

const CooperativeValueFooter: React.FC<FooterProps> = ({ asset, onChange }) => {
	const { t } = useTranslation()
	const realEstateAsset = asset as RealEstate
	const intlConfig = useIntlConfig()
	return (
		<ManualInputContainer>
			<H3>{t('asset-overview.cooperative.value-label')}</H3>
			<BoxInput
				value={realEstateAsset.assetValueAddedByApplicant ?? ''}
				error={realEstateAsset.assetValueAddedByApplicant === undefined}
				onChange={(e) => {
					const raw = parseFloat(e.target.value)
					onChange?.({
						...realEstateAsset,
						assetValueAddedByApplicant: isNaN(raw) ? undefined : raw,
					} as Asset)
				}}
				currencyInputProps={{
					allowDecimals: false,
					allowNegativeValue: false,
					intlConfig,
					maxLength: 10,
				}}
			/>
		</ManualInputContainer>
	)
}

export const AssetCard: React.FC<{
	asset: Asset
	isSelected?: boolean
	hasBorder?: boolean
	onChange?: (asset: Asset) => void
	onDelete?: (asset: Asset) => void
	onSelect?: (isSelected: boolean) => void
	variant?: 'default' | 'preview'
	shouldInputCooperativeHomeValue?: boolean
}> = ({
	asset,
	isSelected,
	onChange = () => {},
	onDelete,
	onSelect,
	children,
	variant = 'default',
	shouldInputCooperativeHomeValue = false,
}) => {
	const [isExpanded, setIsExpanded] = useState(false)

	const initialHomeFinancingType = useMemo(
		() =>
			overarchingTypeOf(asset.assetType) === 'realEstate'
				? (asset as RealEstate).financingType
				: undefined,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)
	const shouldShowFinancingTypeSelector =
		overarchingTypeOf(asset.assetType) === 'realEstate' &&
		variant === 'default' &&
		initialHomeFinancingType === RealEstateFinancingType.None

	const shouldShowCooperativeValueInput =
		shouldInputCooperativeHomeValue &&
		overarchingTypeOf(asset.assetType) === 'realEstate' &&
		variant === 'default' &&
		(asset as RealEstate).financingType === RealEstateFinancingType.Cooperative

	const { openDeleteModal } = useDeleteAssetModal({
		onDelete: (asset) => onDelete?.(asset),
		asset,
	})

	const { description } = getAssetDescription(asset)
	const { t } = useTranslation()

	const fields = useDescriptionFieldsOf(asset)

	const isVehicle = overarchingTypeOf(asset.assetType) === 'vehicle'
	const isRealEstate = overarchingTypeOf(asset.assetType) === 'realEstate'
	const shouldRenderFooter = variant === 'default'

	const assetType = overarchingTypeOf(asset.assetType)

	const Footer: React.FC<FooterProps> | undefined = (() => {
		if (!shouldRenderFooter) return undefined
		if (isVehicle && asset.assetType !== AssetType.CampingWagon) return VehicleFooter
		if (isRealEstate && shouldShowCooperativeValueInput) return CooperativeValueFooter
		if (isRealEstate && shouldShowFinancingTypeSelector) return RealEstateFooter
		return undefined
	})()

	return (
		<ConditionalWrapper
			condition={asset.deletedByApplicant}
			Wrapper={DeletedOverlay}
		>
			<Accordion expanded={isExpanded}>
				<ItemWithOptionalLogo
					title={
						description ||
						t('asset-overview.asset-card.no-title', {
							assetType: translateAssetType(asset.assetType),
						})
					}
					logo={<AssetLogo assetType={asset.assetType} />}
					financingType={
						asset.financingType
							? translateFinancingType(asset.financingType, asset.assetType)
							: undefined
					}
					isRounded
					hasBorder
					isSelected={isSelected}
					onItemSelect={onSelect}
					footerContent={
						Footer ? (
							<Footer
								asset={
									assetType === 'vehicle'
										? (asset as Vehicle)
										: (asset as RealEstate)
								}
								onChange={onChange}
							/>
						) : undefined
					}
				>
					{onDelete && !asset.deletedByApplicant && (
						<IconButton onClick={openDeleteModal}>
							<MaterialIcons.Delete />
						</IconButton>
					)}
					{variant === 'default' && !asset.deletedByApplicant && (
						<>
							<IconButton onClick={() => setIsExpanded((prev) => !prev)}>
								{isExpanded ? (
									<MaterialIcons.KeyboardArrowUpOutlined />
								) : (
									<MaterialIcons.KeyboardArrowDownOutlined />
								)}
							</IconButton>
						</>
					)}
				</ItemWithOptionalLogo>
				{children}
				{isExpanded && (
					<AccordionDetails>
						<FieldsBackground>
							{fields
								.filter((x) => x.value)
								.flatMap(({ title, value }) => [
									<div key={`${title}-key`}>{title}</div>,
									<div key={`${title}-value`}>{value}</div>,
								])}
						</FieldsBackground>
					</AccordionDetails>
				)}
			</Accordion>
		</ConditionalWrapper>
	)
}
